export const javaNotes = [
  {
    id: 4001,
    topic: "java",
    seriesKey: "java",
    title: "Java Fundamentals",
    description:
      "Introduction to Java, its core philosophy (WORA), and the essential components: Java Development Kit, Runtime Environment, and Virtual Machine.",
    content: `
##  Java Core Concepts: The Ecosystem
Java is a **high-level, object-oriented, class-based** programming language designed for **platform independence**.
### Write Once, Run Anywhere (WORA)
**Definition:** Java bytecode, compiled from the source code, can run on any machine that has a Java Virtual Machine (JVM), regardless of the operating system.
 **Significance:** Ensures maximum portability across different platforms (Windows, macOS, Linux, etc.).
### The Java Ecosystem Components
| Component | Definition | Role |
| :--- | :--- | :--- |
| JDK (Java Development Kit) | A software package for writing, compiling, and running Java applications. | For developers; includes the compiler (\`javac\`), debugger, JRE, etc. |
| JRE (Java Runtime Environment) | Provides the libraries and resources required to execute a Java program. | For end-users; includes the JVM and core libraries. |
| JVM (Java Virtual Machine) | The abstract machine that provides the runtime environment for Java bytecode execution. | Provides platform independence by interpreting the bytecode into native machine code. |
\`\`\`bash
# Simple compilation and execution process:
# 1. Source Code (.java) -> Compiler (javac in JDK) -> Bytecode (.class)
# 2. Bytecode (.class) -> JVM (in JRE) -> Native Machine Code (Execution)
\`\`\`
`,
    tags: ["Java", "Basics", "JDK", "JRE", "JVM", "WORA", "Fundamentals"],
    date: "2025-10-21",
  },
  {
    id: 4002,
    topic: "java",
    seriesKey: "java",
    title: "Primitive Data Types and Variables",
    description:
      "Understanding Java's 8 primitive data types (byte, short, int, long, float, double, char, boolean) and how to declare variables.",
    content: `
##  Primitive Data Types
**Primitive Types** are fundamental building blocks that store the data value directly. They are fixed in size and do not require the \`new\` keyword.
### Definition
There are **8 primitive data types** in Java, categorized into **numeric** (integer and floating-point) and **non-numeric** (character and boolean).
| Type | Size (Bytes) | Range/Values | Purpose |
| :--- | :--- | :--- | :--- |
| \`byte\` | 1 | -128 to 127 | Space-saving for large arrays. |
| \`short\` | 2 | -32,768 to 32,767 | Short integer. |
| \`int\` | 4 |  -2,147,483,648 to  2,147,483,647 | Default integer type. |
| \`long\` | 8 | Very large integer. | Large-value computations. |
| \`float\` | 4 | Single-precision floating point. | Requires 'f' or 'F' suffix (e.g., 3.14f). |
| \`double\` | 8 | Double-precision floating point. | Default floating-point type. |
| \`char\` | 2 | Single 16-bit Unicode character. | Representing characters (e.g., 'A', '1'). |
| \`boolean\` | N/A (platform dependent) | \`true\` or \`false\` | Logical conditions. |
### The Universal Formula To Find Primitive Data Range
The range for any n-bit signed integer type in Java is given by:
Range: -2^{n-1} { to } 2^{n-1} - 1
Where **n** is the **number of bits** (the size) of the data type.
** 1 byte = 8 bits **
### Syntax
\`\`\`java
dataType variableName = initialValue;
\`\`\`
### Simple Program Example
\`\`\`java
public class DataTypesExample {
    public static void main(String[] args) {
        // Integer (int is default)
        int age = 30;
        long population = 7900000000L; // 'L' suffix for long

        // Floating Point (double is default)
        double pi = 3.14159;
        float rate = 5.5f; // 'f' suffix for float

        // Character and Boolean
        char initial = 'J';
        boolean isComplete = true;

        System.out.println("Age: " + age);
        System.out.println("Initial: " + initial);
        System.out.println("Is Complete: " + isComplete);
    }
}
\`\`\`
`,
    tags: [
      "Java",
      "Basics",
      "Data Types",
      "Primitive",
      "Variables",
      "int",
      "double",
    ],
    date: "2025-10-21",
  },
  {
    id: 4003,
    topic: "java",
    seriesKey: "java",
    title: "Java Operators and Expressions",
    description:
      "A look at arithmetic, relational, logical, assignment, and the conditional (ternary) operators in Java.",
    content: `
##  Java Operators
Operators are special symbols used to perform operations on variables and values.
### Definition
Operators are categorized by the type of operation they perform (e.g., mathematical, comparison, logical).
| Category | Operators | Description |
| :--- | :--- | :--- |
| Arithmetic | \`+\`, \`-\`, \`*\`, \`/\`, \`%\` | Standard math operations and modulus (remainder). |
| Assignment | \`=\`, \`+=\`, \`-=\`, \`*=\` | Assigns a value to a variable, often performing an operation first. |
| Relational | \`==\`, \`!=\`, \`>\`, \`<\`, \`>=\`, \`<=\` | Compares two values; returns a \`boolean\` result. | 
| Logical | \`&&\` (AND), (OR), \`!\` (NOT) | Used to combine conditional statements. |
| Ternary (Conditional) | \`? :\` | A shorthand for simple \`if-else\` statements. |
### Syntax (Ternary Operator)
\`\`\`java
condition ? value_if_true : value_if_false;
\`\`\`
### Simple Program Example
\`\`\`java
public class OperatorExample {
    public static void main(String[] args) {
        int a = 10;
        int b = 3;

        // Arithmetic
        int sum = a + b; // 13
        int remainder = a % b; // 1 (10 / 3 = 3 with remainder 1)

        // Relational and Logical
        boolean isGreater = (a > b) && (a != 0); // true && true = true

        // Assignment
        a += 5; // a is now 15 (a = a + 5)

        // Ternary Operator
        String result = (sum > 10) ? "Sum is big" : "Sum is small";

        System.out.println("Remainder: " + remainder);
        System.out.println("Is Greater: " + isGreater);
        System.out.println("Ternary Result: " + result); // Output: Sum is big
    }
}
\`\`\`
`,
    tags: ["Java", "Basics", "Operators", "Arithmetic", "Logical", "Ternary"],
    date: "2025-10-21",
  },
  {
    id: 4004,
    topic: "java",
    seriesKey: "java",
    title: "Basic I/O: Output and the Scanner Class",
    description:
      "Performing basic input and output operations in Java, using System.out for printing and the Scanner class for console input.",
    content: `
##  Basic Input/Output (I/O)
### Definition
 **Output:** Displaying information to the console or another destination. The core mechanism is the \`System.out\` object.
 **Input:** Reading data from an external source, typically the console, using the **\`Scanner\`** class (part of \`java.util\` package).
### Output Syntax
| Method | Description | Example |
| :--- | :--- | :--- |
| \`System.out.print()\` | Prints data and does not append a newline. | \`System.out.print("Hello");\` |
| \`System.out.println()\` | Prints data and appends a newline character. | \`System.out.println("World");\` |
### Input Syntax (\`Scanner\`)
The \`Scanner\` class provides methods to read various data types.
\`\`\`java
// 1. Import the class
import java.util.Scanner;
// 2. Create a Scanner object
Scanner scanner = new Scanner(System.in);
// 3. Read input
int num = scanner.nextInt(); // Reads an integer
String line = scanner.nextLine(); // Reads a whole line of text
// 4. Close the scanner
scanner.close();
\`\`\`

### Simple Program Example
\`\`\`java
import java.util.Scanner;

public class IOExample {
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);

        // Output
        System.out.print("Enter your name: ");

        // Input
        String name = scanner.nextLine();

        System.out.print("Enter your age: ");
        // Ensure input for age is an integer
        if (scanner.hasNextInt()) {
            int age = scanner.nextInt();
            System.out.println("Hello, " + name + "! You are " + age + " years old.");
        } else {
            System.out.println("Invalid age entered.");
        }

        // Always close resources
        scanner.close();
    }
}
\`\`\`
`,
    tags: ["Java", "Basics", "I/O", "Scanner", "System.out", "Input", "Output"],
    date: "2025-10-21",
  },
  {
    id: 4005,
    topic: "java",
    seriesKey: "java",
    title: "Type Conversion and Casting",
    description:
      "Explaining implicit (widening) and explicit (narrowing) type conversions between different primitive data types.",
    content: `
##  Type Conversion & Casting
Type conversion is the process of changing a value from one data type to another.
### Definition
1.  **Implicit Conversion (Widening):** Automatic conversion of a smaller data type to a larger data type. This is **safe** as no data is lost.
     **Order:** \`byte\` ➡️ \`short\`  ➡️ \`int\`  ➡️\`long\`  ➡️ \`float\`  ➡️ \`double\`
2.  **Explicit Conversion (Narrowing/Casting):** Manual conversion of a larger data type to a smaller data type. This is **unsafe** and requires a **cast operator** to prevent compile-time errors, as data loss is possible.
### Syntax (Explicit Casting)
\`\`\`java
// Syntax for explicit casting:
dataType_to_convert_to variableName = (dataType_to_convert_to) variable_to_convert;
\`\`\`
### Simple Program Example
\`\`\`java
public class CastingExample {
    public static void main(String[] args) {
        // --- 1. Implicit (Widening) Conversion ---
        int myInt = 100;
        double myDouble = myInt; // Automatic conversion: int to double
        System.out.println("Implicit Conversion (Int to Double): " + myDouble); // Output: 100.0

        // --- 2. Explicit (Narrowing) Conversion / Casting ---
        double largeDouble = 100.99;
        int narrowInt = (int) largeDouble; // Explicit casting: double to int (data is truncated)
        System.out.println("Explicit Conversion (Double to Int): " + narrowInt); // Output: 100

        // Explicit cast with data loss example
        int bigNum = 200;
        byte smallByte = (byte) bigNum; // 200 is outside byte's range (-128 to 127)
        System.out.println("Explicit with Data Loss (Int to Byte): " + smallByte); // Output: -56 (due to overflow)
    }
}
\`\`\`
`,
    tags: ["Java", "Basics", "Casting", "Conversion", "Implicit", "Explicit"],
    date: "2025-10-21",
  },
  {
    id: 4006,
    topic: "java",
    seriesKey: "java",
    title: "Control Flow Statements",
    description:
      "Directing program execution using conditional logic (if, switch) and iteration (for, while, do-while).",
    content: `
##  Control Flow: Conditionals
Control flow statements manage the order of instruction execution.
### Conditional Statements
 1.**\`if\`**, **\`if-else\`**, **\`if-else-if\`**, and **nested \`if\`** structures are used for decision making based on boolean expressions.
 2.**\`switch-case\`**: Used for multi-way branching based on the value of a single variable (often \`int\`, \`char\`, \`String\`, or \`enum\`).
### Syntax (Switch)
\`\`\`java
switch (expression) {
    case value1:
        // code block 1
        break;
    case value2:
        // code block 2
        break;
    default:
        // default code block
}
\`\`\`
* The break statement is crucial to exit the switch block after a case is matched.
### Simple Program Example
\`\`\`java
public class ConditionalsExample {
    public static void main(String[] args) {
        int day = 3;
        String dayName;
        switch (day) {
            case 1: dayName = "Mon"; break;
            case 2: dayName = "Tue"; break;
            case 3: dayName = "Wed"; break;
            default: dayName = "Other";
        }
        System.out.println("The day is: " + dayName); // Output: Wed

        int score = 85;
        if (score >= 90) {
            System.out.println("Grade A");
        } else if (score >= 80) {
            System.out.println("Grade B"); // Output: Grade B
        } else {
            System.out.println("Grade C");
        }
    }
}
\`\`\`
`,
    tags: ["Java", "Control Flow", "if", "switch", "Conditional", "break"],
    date: "2025-10-21",
  },
  {
    id: 4007,
    topic: "java",
    seriesKey: "java",
    title: "Control Flow Statements",
    description:
      "Directing program execution using iteration: for, enhanced for-each, while, and do-while loops, including break and continue.",
    content: `
##  Control Flow: Loops
Loops are used to execute a block of code repeatedly.
### Loop Structures
1. **\`for\` loop:** Used when the number of iterations is known.
2. **Enhanced \`for-each\` loop:** Simplifies iteration over arrays and collections.
3. **\`while\` loop:** Executes a block of code as long as a condition is true (condition check is **before** execution).
4. **\`do-while\` loop:** Executes the block of code at least once, then checks the condition (condition check is **after** execution).
### Loop Control Syntax
1. **\`break\`**: Immediately terminates the innermost loop or \`switch\` statement.
 2.**\`continue\`**: Skips the current iteration of the loop and proceeds to the next iteration.
### Simple Program Example
\`\`\`java
public class LoopExample {
    public static void main(String[] args) {
        int[] numbers = {1, 2, 3, 4, 5, 6};

        // Enhanced for-each loop
        System.out.print("Odd numbers: ");
        for (int n : numbers) {
            if (n % 2 == 0) {
                continue; // Skip even numbers
            }
            System.out.print(n + " "); // Output: 1 3 5 
        }
        System.out.println();

        // While loop with break
        int i = 0;
        while (i < 10) {
            if (i == 5) {
                break; // Stop when i is 5
            }
            System.out.print(i + " "); // Output: 0 1 2 3 4
            i++;
        }
        System.out.println();
    }
}
\`\`\`
`,
    tags: [
      "Java",
      "Control Flow",
      "for loop",
      "while loop",
      "do-while loop",
      "for-each",
      "break",
      "continue",
    ],
    date: "2025-10-21",
  },
  {
    id: 4008,
    topic: "java",
    seriesKey: "java",
    title: " OOPs in Java",
    description:
      "The four pillars of OOP (Encapsulation, Inheritance, Polymorphism, Abstraction) and their implementation in Java classes and interfaces.",
    content: `
##  Java OOP( Object Oriented Programming)
Java is fundamentally **object-oriented**, meaning programs are designed around objects and their interactions.
### Classes and Objects
 1.**Class:** A blueprint for objects, defining **Fields** (attributes/data) and **Methods** (behavior/actions).
 2.**Object:** An instance of a class.
 3.**\`this\` keyword:** Used inside a method or constructor to refer to the current object.
 4.**Constructors:** Special methods used to initialize objects. A class can have multiple constructors (**Method Overloading**).
### The Four Pillars
1. **Encapsulation:** Protecting data by making fields **\`private\`** and providing controlled access via **\`public\` getters and setters\`**.
2. **Inheritance:** A class (**subclass**) acquires the properties and methods of another class (**superclass**) using the **\`extends\`** keyword.* **\`super\` keyword:** Used to refer to the immediate parent class member.
3. **Polymorphism (Many Forms):**
 * Compile-time (Static): Achieved through Method Overloading (same method name, different parameter lists).
 * Runtime (Dynamic): Achieved through Method Overriding (same method signature in subclass as superclass).
4. **Abstraction:** Hiding complex implementation details and showing only essential features. Achieved using **Abstract Classes** and **Interfaces**.
### Access Modifiers
| Modifier | Within Class | Within Package | Outside Package (Subclass) | Outside Package (World) |
| :--- | :--- | :--- | :--- | :--- |
| \`private\` | Yes | No | No | No |
| \`default\` | Yes | Yes | No | No |
| \`protected\` | Yes | Yes | Yes | No |
| \`public\` | Yes | Yes | Yes | Yes |
 ➡️**\`final\` keyword:** Used to make variables constants, prevent method overriding, or prevent class inheritance.
`,
    tags: [
      "Java",
      "OOP",
      "Class",
      "Inheritance",
      "Polymorphism",
      "Encapsulation",
      "Abstract",
      "Interface",
    ],
    date: "2025-10-03",
  },
  {
    id: 4009,
    topic: "java",
    seriesKey: "java",
    title: "Exception Handling",
    description:
      "Handling runtime errors gracefully using try-catch blocks, differentiating between checked and unchecked exceptions, and managing custom exceptions.",
    content: `
##  Exception Handling
A mechanism to handle runtime errors and maintain the normal flow of a program.
### Errors vs. Exceptions
* Errors: Irrecoverable conditions (e.g., \`StackOverflowError\`, \`OutOfMemoryError\`).
* Exceptions: Conditions that can usually be caught and handled.
### Types of Exceptions
1. **Checked Exceptions:** Must be handled using a \`try-catch\` block or declared using the **\`throws\`** keyword. Checked at compile time (e.g., \`IOException\`, \`SQLException\`).
2. **Unchecked Exceptions (Runtime Exceptions):** Not required to be handled. Occur due to programming errors (e.g., \`NullPointerException\`, \`ArithmeticException\`).
### Handling Structure
 1.**\`try\`**: Contains the code that may throw an exception.
 2.**\`catch\`**: Handles the exception if it occurs.
 3.**\`finally\`**: Optional block that always executes, regardless of whether an exception was thrown or caught (used for cleanup). 
 4.**\`throw\`**: Used to explicitly **throw** an exception object from a method.
 5.**\`throws\`**: Used in a method signature to **declare** which checked exceptions the method might throw.
### Custom Exceptions
You can create your own exception classes by extending \`Exception\` (for checked) or \`RuntimeException\` (for unchecked).
\`\`\`java
// Custom Checked Exception Class
class CustomException extends Exception {
    public CustomException(String message) {
        super(message);
    }
}

public class ExceptionExample {
    public void processData(int data) throws CustomException {
        if (data < 0) {
            throw new CustomException("Data cannot be negative.");
        }
    }
    
    public void execute() {
        try {
            // Assume the main method is calling this:
            // new ExceptionExample().execute();
            // processData(-5); 
            // The actual test call is omitted for brevity but the principle remains.
            
            // Example of a built-in unchecked exception:
            int result = 10 / 0; // Throws ArithmeticException (Unchecked)
            
        } catch (ArithmeticException e) {
             System.out.println("Caught an unchecked exception: " + e.getMessage()); // Output: Caught an unchecked exception: / by zero
        } catch (CustomException e) {
            System.out.println("Caught a checked exception: " + e.getMessage());
        } finally {
            System.out.println("Cleanup complete."); 
        }
    }
}
\`\`\`
`,
    tags: [
      "Java",
      "Exceptions",
      "try-catch",
      "Checked",
      "Unchecked",
      "throw",
      "throws",
    ],
    date: "2025-10-03",
  },
  {
    id: 4010,
    topic: "java",
    seriesKey: "java",
    title: "Java APIs and Libraries",
    description:
      "A brief overview of key packages in the Java Standard Edition (SE) API for common programming tasks.",
    content: `
##  Java Standard Libraries
The Java API (Application Programming Interface) is a vast collection of pre-written classes and interfaces.
### Key Packages
1. **\`java.lang\` (Automatically Imported):** Fundamental classes like **\`Object\`**, **Wrapper Classes** (\`Integer\`, \`Double\`), **\`String\`**, and **\`Math\`**.
2. **\`java.util\`:** Essential utilities, including the **Collections Framework** (e.g., \`ArrayList\`, \`HashMap\`), **Date/Calendar** (legacy), and the **\`Scanner\`** class.
3. **\`java.io\`:** Classes for input and output operations, dealing with file systems, streams, readers, and writers (e.g., \`File\`, \`BufferedReader\`, \`InputStream\`).
4. **\`java.nio\` (New I/O):** Offers non-blocking I/O, **Buffers**, and **Channels** for high-performance I/O operations.
5. **\`java.time\` (Java 8+):** The modern, immutable date and time API (Joda-Time inspired). Includes **\`LocalDate\`**, **\`LocalTime\`**, **\`LocalDateTime\`**, and objects for measuring time differences like **\`Period\`** and **\`Duration\`**.
`,
    tags: [
      "Java",
      "API",
      "java.lang",
      "java.util",
      "java.io",
      "java.nio",
      "java.time",
      "Collections",
    ],
    date: "2025-10-03",
  },
  // Original ID 4006 (now 4011)
  {
    id: 4011,
    topic: "java",
    seriesKey: "java",
    title: "Advanced OOP Concepts",
    description:
      "Exploring Nested classes, Enums, Generics, variable arguments (Varargs), and other complex object-oriented features.",
    content: `
##  Advanced OOP Concepts
### Nested Classes
Classes defined inside another class.
 1.**Inner Class (Non-static):** Associated with an instance of the outer class.
 2.**Static Nested Class:** Associated with the outer class itself, not its instances.
 3.**Anonymous Inner Class:** Used to implement an interface or extend a class once, without explicitly naming it (often used with listeners).
### Enum Types
- A special class type that enables you to define a set of named constant values (e.g., \`public enum Day { MONDAY, TUESDAY }\`). Enums can have constructors, fields, and methods.
### Generics
- Allow classes, interfaces, and methods to operate on objects of various types while providing compile-time type safety. They use type parameters (e.g., \`<T>\`).
### Varargs (Variable Arguments)
- Allows a method to accept zero or more arguments of a specified type using ellipsis (\`...\`). The arguments are passed as an array inside the method.
\`\`\`java
public static void printArgs(String... names) {
    // names is treated as String[] inside the method
    for (String name : names) {
        System.out.println(name);
    }
}
\`\`\`
### Reflection and Serialization
1.**Reflection API:** A powerful feature that allows a running Java program to examine or modify the behavior of classes, interfaces, fields, and methods at runtime.
2.**Serialization:** The process of converting an object into a sequence of bytes (often for storage or transmission). Requires the class to implement the **\`Serializable\`** interface.
`,
    tags: [
      "Java",
      "Advanced OOP",
      "Nested Classes",
      "Enum",
      "Generics",
      "Varargs",
      "Reflection",
      "Serialization",
    ],
    date: "2025-10-03",
  },
  // Original ID 4007 (now 4012)
  {
    id: 4012,
    topic: "java",
    seriesKey: "java",
    title: "Multithreading and Concurrency",
    description:
      "Creating and managing multiple threads of execution, handling synchronization to prevent data race conditions, and using the Executor Framework.",
    content: `
##  Multithreading & Concurrency
**Multithreading** allows a program to execute multiple parts concurrently, improving throughput and responsiveness.
### Creating Threads
1. **Extend \`Thread\` class:** Simple but restricts class from extending other classes.
2. **Implement \`Runnable\` interface:** Recommended approach, as it separates task logic from thread management.
### Thread Lifecycle
New ➡️ Runnable ➡️ Running ➡️ Blocked/Waiting ➡️ Terminated.
### Synchronization
The **\`synchronized\`** keyword is used to protect shared resources (critical sections) from being accessed by multiple threads simultaneously, preventing data inconsistency (race conditions). It can be applied to methods or blocks of code.
### Inter-thread Communication
Achieved using methods inherited from the \`Object\` class, always within a synchronized block:
1. **\`wait()\`**: Causes the current thread to release the lock and wait until another thread invokes \`notify()\` or \`notifyAll()\`.
2. **\`notify()\`**: Wakes up a single waiting thread.
3. **\`notifyAll()\`**: Wakes up all waiting threads.
### Concurrency Utilities
 **Executor Framework:** A set of interfaces and classes (\`Executor\`, \`ExecutorService\`, \`Executors\`) for managing thread pools, simplifying concurrency management and efficiently reusing threads.
 **Thread-safe Collections:** Collections designed for concurrent access, such as \`ConcurrentHashMap\` or classes from the \`java.util.concurrent\` package.
`,
    tags: [
      "Java",
      "Multithreading",
      "Concurrency",
      "Runnable",
      "synchronized",
      "wait",
      "notify",
      "Executor",
    ],
    date: "2025-10-03",
  },
  // Original ID 4008 (now 4013)
  {
    id: 4013,
    topic: "java",
    seriesKey: "java",
    title: "The Collections Framework",
    description:
      "In-depth look at Java's robust set of interfaces (List, Set, Queue, Map) and their various implementation classes, along with sorting mechanisms.",
    content: `
##  Collections Framework
The Collections Framework provides unified interfaces and implementations for manipulating collections of objects.
### Core Interfaces
1. **List (Ordered, Duplicates Allowed):** Implemented by \`ArrayList\` (resizable array, fast access $O(1)$) and \`LinkedList\` (doubly linked list, fast insertion/deletion $O(1)$).
2. **Set (Unordered, No Duplicates):** Implemented by \`HashSet\` (hash table based, fast $O(1)$) and \`TreeSet\` (tree structure, ordered, $O(log N)$).
3. **Queue (FIFO - First-In, First-Out):** Implemented by \`LinkedList\` and \`PriorityQueue\` (elements ordered by priority).
4. **Map (Key-Value Pairs, Keys Unique):** Implemented by \`HashMap\` (hash table based, fast $O(1)$) and \`TreeMap\` (tree structure, keys ordered, $O(log N)$).
### Sorting and Ordering
1. **\`Iterator\`**: Allows sequential access to elements in a collection.
2. **\`Comparable\`**: Used for defining the natural sorting order within an object's class (single sort field). Implements the \`compareTo()\` method.
3. **\`Comparator\`**: Used for defining custom sorting orders outside the class (multiple sort fields). Implements the \`compare()\` method.
### Generics in Collections
- Generics are heavily used in the Collections Framework to enforce type safety at compile time (e.g., \`List<String>\`).
`,
    tags: [
      "Java",
      "Collections",
      "List",
      "Set",
      "Map",
      "ArrayList",
      "HashMap",
      "Comparable",
      "Comparator",
      "Generics",
    ],
    date: "2025-10-03",
  },
  // Original ID 4009 (now 4014)
  {
    id: 4014,
    topic: "java",
    seriesKey: "java",
    title: "Functional Programming",
    description:
      "Using Lambda Expressions, Functional Interfaces, and the Streams API to write declarative, functional code in Java.",
    content: `
## ➿ Functional Programming (Java 8+)
Java 8 introduced features to support functional programming paradigms.
### Lambda Expressions
A concise way to represent an anonymous function (a method without a name). Used primarily to implement functional interfaces.
\`\`\`java
// After Java 8 (Lambda)
Runnable r = () -> System.out.println("Running with Lambda");
\`\`\`
### Functional Interfaces
An interface with exactly one abstract method (SAM - Single Abstract Method). They are used as the type for Lambda expressions. They are annotated with \`@FunctionalInterface\`. Common examples: \`Runnable\`, \`Callable\`, \`Consumer\`, \`Predicate\`, \`Function\`.
### Streams API
A sequence of elements from a source (like a Collection or array) that supports aggregate operations (non-terminal operations are chained, terminal operations produce a result).
1. **Intermediate Operations (non-terminal):** \`filter()\`, \`map()\`, \`distinct()\`, \`sorted()\` (return a new stream).
2. **Terminal Operations:** \`forEach()\`, \`reduce()\`, \`collect()\`, \`count()\` (produce a result or side effect).
### Optional Class
A container object that may or may not contain a non-null value. Used to represent the absence of a value and help developers avoid \`NullPointerException'\`.
`,
    tags: [
      "Java",
      "Functional",
      "Lambda",
      "Streams",
      "Optional",
      "Method References",
      "Functional Interface",
    ],
    date: "2025-10-03",
  },
  {
    id: 4015,
    topic: "java",
    seriesKey: "java",
    title: "File Handling and I/O Streams",
    description:
      "Working with Byte and Character streams, using Readers/Writers for text data, and leveraging the modern java.nio.file API for file operations.",
    content: `
##  File Handling & I/O
Java I/O operations are based on streams, which are sequences of data flow.
### Stream Types
1. **Byte Streams:** Handle raw binary data (e.g., images, sound). Base classes are **\`InputStream\`** and **\`OutputStream\`**.
2. **Character Streams:** Handle 16-bit Unicode characters (text data). Base classes are **\`Reader\`** and **\`Writer\`**.
3. **Text File Handling:** Use \`FileReader/FileWriter\` for basic character I/O. Use buffered wrappers like **\`BufferedReader\`** and **\`BufferedWriter\`** for faster performance.
### Serialization Streams
 **\`ObjectOutputStream\`** and **\`ObjectInputStream\`** are used for writing and reading entire objects to/from a stream, provided the objects' class implements \`Serializable\`.
### The \`java.nio.file\` API (Modern I/O)
* Provides better performance and more intuitive file system interaction compared to \`java.io.File\`.
1. **\`Paths\`** class: Used to obtain a \`Path\` object.
 2.**\`Files\`** class: Contains static methods for file operations (e.g., \`Files.readAllLines()\`, \`Files.copy()\`).
`,
    tags: [
      "Java",
      "I/O",
      "Byte Streams",
      "Character Streams",
      "BufferedReader",
      "nio",
      "Serialization",
    ],
    date: "2025-10-03",
  },
  {
    id: 4016,
    topic: "java",
    seriesKey: "java",
    title: "JDBC and Database Connectivity",
    description:
      "Connecting Java applications to relational databases, executing CRUD operations, and managing transactions using the JDBC API.",
    content: `
##  JDBC (Java Database Connectivity)
JDBC is an API that defines how a Java client can connect to and interact with a database.
### Core Steps
1. **Loading Driver:** Register the appropriate JDBC driver (e.g., for MySQL, PostgreSQL).
2. **Connecting to DB:** Establish a connection using \`DriverManager.getConnection(url, user, password)\`.
3. **Creating Statement:** Create objects to execute SQL queries.
### Statement Types
1. **\`Statement\`**: Used for simple SQL queries without parameters. Prone to **SQL Injection**.
2. **\`PreparedStatement\`**: Used for pre-compiled SQL queries with placeholders (\`?\`). Highly recommended for performance and security against SQL injection.
3. **CRUD Operations:** Executed using methods like \`execute()\`, \`executeQuery()\` (for SELECT), and \`executeUpdate()\` (for INSERT, UPDATE, DELETE).
### Processing Results and Transactions
1. **\`ResultSet\`**: An object that stores the data retrieved after executing a SELECT query.
2. **Transactions:** A logical unit of work. Managed by turning off auto-commit (\`connection.setAutoCommit(false)\`) and using \`connection.commit()\` or \`connection.rollback()\` to finalize or undo changes.
`,
    tags: [
      "Java",
      "JDBC",
      "Database",
      "PreparedStatement",
      "ResultSet",
      "CRUD",
      "Transactions",
    ],
    date: "2025-10-03",
  },
  {
    id: 4017,
    topic: "java",
    seriesKey: "java",
    title: "Java GUI and Swing ",
    description:
      "Building desktop applications using the Swing library, managing components, and handling user interactions with event listeners.",
    content: `
## 🖥️ Java GUI & Swing
While often superseded by web technologies, Java GUI libraries are used for desktop applications.
### AWT vs. Swing
1. **AWT (Abstract Window Toolkit):** Java's original GUI toolkit. **Heavyweight** components (relies on underlying operating system).
2. **Swing:** Built on top of AWT. **Lightweight** components (drawn entirely in Java, platform-independent look-and-feel).
### Core Swing Components
1. **\`JFrame\`**: The main window/container for the GUI application.
2. **\`JPanel\`**: A general-purpose container for grouping components.
3. **\`JButton\`**, **\`JTextField\`**, **\`JTable\`**, **\`JLabel\`**: Common interactive elements.
### Event Handling
The process of responding to user interactions (events like button clicks, mouse movements, key presses).
1. **Listeners:** Interfaces that must be implemented to handle specific events (e.g., **\`ActionListener\`**, **\`MouseListener\`**, **\`KeyListener\`**).
### Layout Managers
Used to arrange components within a container (e.g., \`JFrame\` or \`JPanel\`).
1. **\`FlowLayout\`**: Components flow in one direction (left-to-right).
2. **\`BorderLayout\`**: Arranges components into five regions: North, South, East, West, Center.
3. **\`GridLayout\`**: Arranges components in a grid (rows and columns).
`,
    tags: [
      "Java",
      "GUI",
      "Swing",
      "AWT",
      "JFrame",
      "Event Handling",
      "Layout Manager",
    ],
    date: "2025-10-03",
  },
  {
    id: 4018,
    topic: "java",
    seriesKey: "java",
    title: "Advanced Concepts",
    description:
      "Coverage of Java Annotations, Generics in depth, memory management, JVM internals, and common design patterns.",
    content: `
##  Advanced Java
### Java Annotations
Metadata added to source code using the \`@\` symbol (e.g., \`@Override\`, \`@Deprecated\`). They don't affect program execution directly but provide information for compilers, frameworks (like Spring), and runtime processing via Reflection.
### Generics in Depth
- Deep understanding includes Wildcards (\`?\`), Bounded Types (\`<T extends Comparable>\`), and the concept of Type Erasure (Generics exist only at compile time).
### Java Memory Management
1. **Heap:** Area where objects are stored. Garbage Collection operates here.
2. **Stack:** Area where local variables and method calls (stack frames) are stored.
3. **Garbage Collection (GC):** Automatic process of reclaiming memory occupied by objects that are no longer referenced.
### JVM Internals
1. **Class Loader Subsystem:** Loads, links, and initializes class files.
2. **Runtime Data Area:** Includes Method Area, Heap, Stack, PC Registers, and Native Method Stacks.
3. **JIT (Just-In-Time) Compiler:** A component of the JVM that compiles frequently executed bytecode into native machine code at runtime for performance optimization.
### Design Patterns
Reusable solutions to common problems in software design:
1. **Creational:** Singleton, Factory, Builder.
2. **Structural:** Adapter, Decorator.
3. **Behavioral:** Observer, Strategy, Iterator.
### Networking
Basic networking involves using **\`Socket\`** for client connections and **\`ServerSocket\`** for server-side listening.
`,
    tags: [
      "Java",
      "Advanced",
      "Annotations",
      "Generics",
      "JVM",
      "GC",
      "JIT",
      "Design Patterns",
      "Networking",
    ],
    date: "2025-10-03",
  },
  {
    id: 4019,
    topic: "java",
    seriesKey: "java",
    title: "Testing and Best Practices",
    description:
      "Introduction to unit testing with JUnit, using logging frameworks, and adhering to Java coding conventions and exception handling best practices.",
    content: `
##  Testing & Best Practices
### JUnit Testing
The de facto standard framework for writing **unit tests** in Java.
1. **Assertions:** Methods used to verify expected outcomes (e.g., \`assertEquals()\`, \`assertTrue()\`, \`assertNull()\`).
2. **Annotations:** Used to define test methods (\`@Test\`), setup (\`@BeforeEach\`), and teardown (\`@AfterEach\`).
\`\`\`java
import org.junit.jupiter.api.Test;
import static org.junit.jupiter.api.Assertions.assertEquals;

public class MyTest {
    @Test
    void testAddition() {
        int result = 2 + 2;
        assertEquals(4, result, "2 + 2 should equal 4");
    }
}
\`\`\`
### Logging
Essential for debugging and monitoring production applications.
1. **\`java.util.logging\`**: Java's built-in logging framework.
2. **SLF4J (Simple Logging Facade for Java):** A facade that allows the user to plug in the desired logging framework (e.g., Logback, Log4j).
### Code Conventions
* Adhering to community-accepted standards (like Oracle's official Java code conventions or Google's style guide) ensures readability and maintainability.
* Key conventions: CamelCase for variables/methods,  PascalCase for classes, ALL CAPS for constants.
### Exception Best Practices
1. **Throw Early, Catch Late:** Throw exceptions as soon as an error is detected, but only catch them where they can be meaningfully handled (or logged).
2. **Specific Catching:** Catch specific exceptions rather than the generic \`Exception\` class.
3. **Clean Up:** Always use the \`finally\` block or **try-with-resources** (Java 7+) to close resources (streams, connections).
`,
    tags: [
      "Java",
      "Testing",
      "JUnit",
      "Assertions",
      "Logging",
      "Best Practices",
    ],
    date: "2025-10-03",
  },
  {
    id: 4020,
    topic: "java",
    seriesKey: "java",
    title: "Projects and Homeworks",
    description:
      "Practical application exercises and project ideas spanning all major Java topics to reinforce learning.",
    content: `
##  Practical Projects and Homework
### Basic & Control Flow (Notes 4001-4007)
 **Homework: Calculator Program:** Create a simple console application that takes two numbers and an operator (\`+\`, \`-\`, \`*\`, \`/\`) as input and uses **\`if-else-if\`** or **\`switch\`** statements to print the result. Include basic **exception handling** for division by zero.
 **Project: Guessing Game:** Implement a loop (\`while\` or \`do-while\`) where the computer generates a random number and the user has a limited number of attempts to guess it. Provide hints (higher/lower).
### OOP & Collections (Notes 4008, 4013)
 **Project: Simple Inventory Management System (OOP Core):**
    1.  Create an **Abstract Class** \`Product\` with fields like \`name\`, \`price\`, and an abstract method \`displayDetails()\`.
    2.  Create subclasses like \`Electronics\` and \`Apparel\` that **extend** \`Product\` and implement \`displayDetails()\` (**Polymorphism/Inheritance**).
    3.  Use an **\`ArrayList<Product>\`** to store all products (**Collections**).
    4.  Implement **Getters/Setters** to protect the price field (**Encapsulation**).

### Advanced & API (Notes 4014-4019)
 **Project: File Analyzer:** Write a program using **\`java.nio.file\`** or **\`java.io\`** to read a text file. Use the **Streams API** to perform aggregate operations like:
    * Count the total number of words.
    * Count the frequency of each unique word (\`HashMap\`).
    * Filter and list all lines containing a specific keyword.
* Advanced Project: Basic Thread Pool Executor: Use the Executor Framework (\`Executors.newFixedThreadPool()\`) to manage a set of worker threads. Create multiple \`Runnable\` tasks (e.g., simulating a long-running calculation) and submit them to the pool to see concurrent execution.
`,
    tags: [
      "Java",
      "Projects",
      "Homework",
      "Practice",
      "OOP Project",
      "Streams Project",
    ],
    date: "2025-10-21",
  },
];
